import { z } from 'zod';

export const AiCacheQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  serviceName: z.string().optional(),
  threadId: z.string().min(1).optional(),
  model: z.string().optional(),
  provider: z.enum(['bedrock', 'vertex', 'gateway']).optional(),
  environment: z.enum(['development', 'staging', 'production']).optional(),
  granularity: z.enum(['hour', 'day']).optional(),
});

export const AiCacheTotalsSchema = z.object({
  invocations: z.number(),
  cacheActiveInvocations: z.number(),
  uncachedInputTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  costUsd: z.number(),
  cacheSavingsUsd: z.number(),
  cacheWritePremiumUsd: z.number(),
  netSavingsUsd: z.number(),
  /** Cache reads as a share of the prompt tokens that could have been cached. */
  hitRatePct: z.number().nullable(),
  /** Cache writes per cache read. Above 1 means prompts are re-cached faster than they are reused. */
  thrashRatio: z.number().nullable(),
});

export const AiCacheSummarySchema = AiCacheTotalsSchema.extend({
  /**
   * Share of cache-active invocations that carry persisted cache economics.
   * Records written before economics were tracked report zero dollars.
   */
  economicsCoveragePct: z.number().nullable(),
});

export const AiCacheBreakdownItemSchema = AiCacheTotalsSchema.extend({
  key: z.string(),
  label: z.string(),
});

export const AiCacheTrendPointSchema = z.object({
  date: z.string(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  uncachedInputTokens: z.number(),
  netSavingsUsd: z.number(),
  hitRatePct: z.number().nullable(),
});

export const AI_CACHE_ANOMALY_KINDS = [
  'hit_rate_drop',
  'cache_thrash',
  'no_cache_usage',
] as const;

export const AiCacheAnomalySchema = z.object({
  kind: z.enum(AI_CACHE_ANOMALY_KINDS),
  severity: z.enum(['warning', 'critical']),
  scope: z.enum(['overall', 'service']),
  subject: z.string(),
  detail: z.string(),
  observedValue: z.number(),
  baselineValue: z.number().nullable(),
});

export const AI_CACHE_NODE_DIMENSIONS = ['agent', 'node', 'nodePath'] as const;

export const AiCacheNodeDimensionSchema = z.enum(AI_CACHE_NODE_DIMENSIONS);

export const AiCacheOverviewSchema = z.object({
  summary: AiCacheSummarySchema,
  byProvider: z.array(AiCacheBreakdownItemSchema),
  byService: z.array(AiCacheBreakdownItemSchema),
  byModel: z.array(AiCacheBreakdownItemSchema),
  byThread: z.array(AiCacheBreakdownItemSchema),
  byTenant: z.array(AiCacheBreakdownItemSchema),
  byAgent: z.array(AiCacheBreakdownItemSchema),
  byNode: z.array(AiCacheBreakdownItemSchema),
  byNodePath: z.array(AiCacheBreakdownItemSchema),
  trend: z.array(AiCacheTrendPointSchema),
  anomalies: z.array(AiCacheAnomalySchema),
});

export type AiCacheQuery = z.infer<typeof AiCacheQuerySchema>;
export type AiCacheTotals = z.infer<typeof AiCacheTotalsSchema>;
export type AiCacheSummary = z.infer<typeof AiCacheSummarySchema>;
export type AiCacheBreakdownItem = z.infer<typeof AiCacheBreakdownItemSchema>;
export type AiCacheTrendPoint = z.infer<typeof AiCacheTrendPointSchema>;
export type AiCacheAnomaly = z.infer<typeof AiCacheAnomalySchema>;
export type AiCacheNodeDimension = z.infer<typeof AiCacheNodeDimensionSchema>;
export type AiCacheOverview = z.infer<typeof AiCacheOverviewSchema>;
